import {defaultFilterProviderOptions} from '../constants/defaults'
import {FILTER_KEYS} from '../constants/filter-constants'
import type {FilterQuery} from '../filter-query'
import type {
  AnyBlock,
  ARIAFilterSuggestion,
  FilterBlock,
  FilterConfig,
  FilterKey,
  FilterProvider,
  IndexedBlockValueItem,
  MutableFilterBlock,
  SuppliedFilterProviderOptions,
  ValueRowProps,
} from '../types'
import {
  capitalize,
  getExcludeKeySuggestion,
  getHasValueSuggestion,
  getLastFilterBlockValue,
  getNoValueSuggestion,
  isEmptyFilterBlockValue,
  isFilterBlock,
} from '../utils'
import {RootFilterProvider} from './root'

export class KeyOnlyFilterProvider extends RootFilterProvider implements FilterProvider {
  constructor(filterKey: FilterKey, options?: SuppliedFilterProviderOptions) {
    super(filterKey, options)
    Object.assign(this, filterKey)
    this.key = filterKey.key
    this.icon = filterKey.icon
    this.displayName = filterKey.displayName ?? capitalize(filterKey.key)
    this.options = {
      ...defaultFilterProviderOptions,
      ...options,
      filterTypes: {
        ...defaultFilterProviderOptions.filterTypes,
        multiValue: false,
        valueless: false,
        ...options?.filterTypes,
      },
    }
    this.priority = this.options.priority
  }

  getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    if (!this.shouldGetSuggestions(filterBlock)) return null

    const lastValue = getLastFilterBlockValue(filterBlock, caretIndex)
    const filterProviderKey = (filterBlock as FilterBlock).provider.key
    const suggestions: ARIAFilterSuggestion[] = []
    if (
      isEmptyFilterBlockValue(filterBlock) &&
      this.options.filterTypes.valueless &&
      !filterBlock.raw.startsWith('-')
    ) {
      suggestions.push(getNoValueSuggestion(this.displayName, this.icon))
    }

    if (isEmptyFilterBlockValue(filterBlock) && this.options.filterTypes.hasValue && !filterBlock.raw.startsWith('-')) {
      suggestions.push(getHasValueSuggestion(this.displayName, this.icon))
    }

    if (
      this.options.filterTypes.exclusive &&
      lastValue === '' &&
      isFilterBlock(filterBlock) &&
      !filterBlock.raw.startsWith('-')
    ) {
      const excludeKeySuggestion = getExcludeKeySuggestion(filterProviderKey)
      suggestions.unshift(excludeKeySuggestion)
    }
    return suggestions
  }

  validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
  ): IndexedBlockValueItem[] {
    return values.map(value => {
      return {
        ...value,
        valid: true,
      }
    })
  }

  getValueRowProps(): ValueRowProps {
    return {text: ''}
  }
}

export class BaseFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.base, options)
  }
}

export class HeadFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.head, options)
  }
}

export class InBodyFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.inBody, options)
  }
}

export class InCommentsFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.inComments, options)
  }
}

export class InTitleFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.inTitle, options)
  }
}

export class ShaFilterProvider extends KeyOnlyFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.sha, options)
  }
}
